import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button as MuiButton,
    Switch,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import PageHeader1 from '../../components/common/PageHeader1';
import axios from 'axios';
import toast from 'react-hot-toast';
import { usePermissions } from '../../context/AuthContext';

function GooglePayCoupon() {
    const { can } = usePermissions();
    const token = localStorage.getItem("vedhvidhi-admin-token");

    const [tabValue, setTabValue] = useState(0);
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [filterModel, setFilterModel] = useState({
        items: [],
        quickFilterExcludeHiddenColumns: true,
        quickFilterValues: [''],
    });

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [paginationDetail, setPaginationDetail] = useState({ totalDocs: 0, totalPages: 0, page: 1, limit: 10 });

    const getCategoryParam = () => {
        if (tabValue === 0) return 'flat_off_rupees'; // Education tab
        if (tabValue === 1) return 'flat_off_percentage'; // Consultation tab
        return '';
    };

    const getTypeParam = () => {
        if (tabValue === 0) return 'education'; // Education tab
        if (tabValue === 1) return 'consultation'; // Consultation tab
        return '';
    };

    const fetchCoupons = async (page = 1, limit = 10, type = '') => {
        if (!token) {
            setError("Authentication token not found.");
            return;
        }
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/coupon?page=${page}&limit=${limit}&type=${type}&isGooglePay=true`,
                { headers: { Authorization: `${token}` } }
            );
            if (response.data.success) {
                setCoupons(response.data.data || []);
                setPaginationDetail(response.data.paginationDetail || { totalDocs: 0, totalPages: 0, page: 1, limit: 10 });
            } else {
                setError("Failed to fetch coupons");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const type = getTypeParam();
        fetchCoupons(paginationModel.page + 1, paginationModel.pageSize, type);
    }, [tabValue, paginationModel]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const handleClickOpen = (userId) => {
        setSelectedUserId(userId);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setSelectedUserId(null);
    };

    const handleDelete = async () => {
        if (!selectedUserId) return;
        setLoading(true);
        try {
            await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/coupon/${selectedUserId}`, {
                headers: { Authorization: `${token}` },
            });
            toast.success("Coupon deleted successfully!");
            const type = getTypeParam();
            fetchCoupons(paginationModel.page + 1, paginationModel.pageSize, type);
        } catch (err) {
            toast.error("Failed to delete coupon.");
        } finally {
            setLoading(false);
            handleClose();
        }
    };

    const handleToggle = async (couponId, currentStatus) => {
        try {
            await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/coupon/${couponId}`, { isActive: !currentStatus }, {
                headers: { Authorization: `${token}` },
            });
            const type = getTypeParam();
            fetchCoupons(paginationModel.page + 1, paginationModel.pageSize, type);
            toast.success("Status updated successfully!");
        } catch (err) {
            console.error("Toggle error:", err);
            toast.error("Failed to update status.");
        }
    };

    const formatDateOnly = (dateString) => {
        if (!dateString) return "N/A";
        // Extract date part (YYYY-MM-DD) from ISO string
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
    };

    const rows = coupons.map((item, index) => ({
        id: item._id,
        sNo: index + 1 + paginationModel.page * paginationModel.pageSize,
        category: item.category || "N/A",
        discountValue: item.discountValue || 0,
        isActive: item.isActive || false,
        validFrom: formatDateOnly(item.validFrom),
        validTo: formatDateOnly(item.validTo),
        minCartValue: item.minCartValue || 0,
        createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString() : "",
    }));

    const columns = [
        { field: 'sNo', headerName: 'S.No.', width: 80 },
        { field: 'category', headerName: 'Category', width: 150 },
        { field: 'discountValue', headerName: 'Discount', width: 150 },
        { field: 'validFrom', headerName: 'Valid From', width: 150 },
        { field: 'validTo', headerName: 'Valid To', width: 150 },
        { field: 'minCartValue', headerName: 'Minimum Order', width: 150 },
        {
            field: 'isActive',
            headerName: 'Active',
            width: 150,
            renderCell: ({ row }) => (
                <Switch
                    checked={row.isActive}
                    onChange={() => handleToggle(row.id, row.isActive)}
                    disabled={!can('google_pay_coupons:edit')}
                />
            ),
        },
        { field: 'createdAt', headerName: 'Created At', width: 200 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: ({ row }) => (
                <div>
                    <Tooltip title="View Coupon">
                        <IconButton component={Link} to={process.env.PUBLIC_URL + `/google-view/${row?.id}`}>
                            <i className="icofont-eye text-info"></i>
                        </IconButton>
                    </Tooltip>
                    {can('google_pay_coupons:delete') && (
                        <Tooltip title="Delete Coupon">
                            <IconButton onClick={() => handleClickOpen(row?.id)}>
                                <i className="icofont-ui-delete text-danger"></i>
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="body d-flex py-1">
            <div className="container-xxl">
                <PageHeader1
                    pagetitle='Google Pay Coupon List'
                    righttitle='Add Coupon'
                    link='/googlepaycoupon-add'
                    routebutton={can('google_pay_coupons:add')}
                />

                <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Education" />
                        <Tab label="Consultation" />
                    </Tabs>
                </Box>

                <div className="row g-0 mb-3">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                {error ? (
                                    <p className="text-danger">{error}</p>
                                ) : (
                                    <Box sx={{ height: 500, width: '100%' }}>
                                        <DataGrid
                                            loading={loading}
                                            rows={rows}
                                            columns={columns}
                                            getRowId={(row) => row.id}
                                            disableColumnFilter
                                            disableDensitySelector
                                            slots={{ toolbar: GridToolbar }}
                                            paginationMode="server"
                                            rowCount={paginationDetail.totalDocs}
                                            paginationModel={paginationModel}
                                            onPaginationModelChange={setPaginationModel}
                                            pageSizeOptions={[10, 20, 50, 100]}
                                            filterModel={filterModel}
                                            onFilterModelChange={setFilterModel}
                                            slotProps={{ toolbar: { showQuickFilter: true } }}
                                            columnVisibilityModel={columnVisibilityModel}
                                            onColumnVisibilityModelChange={setColumnVisibilityModel}
                                        />
                                    </Box>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete this coupon?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleClose}>Cancel</MuiButton>
                    <MuiButton onClick={handleDelete} color="error" disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : "Delete"}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default GooglePayCoupon;